import {
  useRef,
  useMemo,
  useState,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import Cropper from 'react-easy-crop';
import type {
  Area,
} from 'react-easy-crop';
import classNames from 'classnames';
import Slider from '@/ui/components/Slider';

import styles from './ImageCropper.module.css';

type ImageCropperProps = {
  image: string;
  min?: number;
  max?: number;
  step?: number;
  aspect?: number;
  showGrid?: boolean;
  maxHeight?: string;
  cropShape?: 'rect' | 'round';
  objectFit?: 'cover' | 'contain' | 'horizontal-cover' | 'vertical-cover';
  restrictPosition?: boolean;
  flat?: boolean;
  onCropComplete?: (croppedArea: Area, croppedAreaPixels: Area) => void;
}

const DEFAULT_ZOOM = 1;
const DEFAULT_CROP = {
  x: 0,
  y: 0,
};

const ImageCropper = (props: ImageCropperProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    image,
    aspect,
    min = 1,
    max = 3,
    step = 0.1,
    showGrid = false,
    maxHeight,
    cropShape = 'rect',
    objectFit = 'contain',
    restrictPosition,
    flat = false,
    onCropComplete,
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const cropperRef = useRef(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [crop, setCrop] = useState(DEFAULT_CROP);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const {
    clientWidth = 1,
    clientHeight = 1,
  } = cropperRef.current || {};
  const aspectRatio = aspect || clientWidth / clientHeight;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleCropperRef = (ref) => cropperRef.current = ref;

  const handleZoomChange = (e: ChangeEvent<HTMLInputElement>) => (
    setZoom(Number(e.target.value))
  );

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const imageCropperClassName = classNames({
    [styles.imageCropper]: true,
    [styles.flat]: flat,
  });

  const imageCropperAreaStyle = useMemo(() => ({
    maxHeight,
    aspectRatio,
  }), [maxHeight]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={imageCropperClassName}>
      <div
        ref={cropperRef}
        style={imageCropperAreaStyle}
        className={styles.area}
      >
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          setCropperRef={handleCropperRef}
          showGrid={showGrid}
          cropShape={cropShape}
          objectFit={objectFit}
          restrictPosition={restrictPosition}
        />
      </div>

      <div className={styles.zoom}>
        <Slider
          value={zoom}
          min={min}
          max={max}
          step={step}
          onChange={handleZoomChange}
        />
      </div>
    </div>
  );
};

export default ImageCropper;
